/* NCMPRBL Warranty Registration - assets/warranty.js */
(() => {
"use strict";
/* ==========================================================================
   NCMPRBL — Warranty Registration → Klaviyo
   --------------------------------------------------------------------------
   How submission works (two client-side Klaviyo calls, both CORS-enabled):

   1. ALWAYS  → POST /client/profiles/
      Creates/updates the profile with custom properties for channel
      attribution (product, retailer, purchase date). Runs for every
      registrant, whether or not they opt into marketing.

   2. IF opted in → POST /client/subscriptions/
      Records explicit marketing consent for the channels the customer
      checked (email and/or SMS, sent separately in one call) and adds
      the profile to the list below.

   NOTE: The target list is currently "NCMPRBLife Community", which is
   configured for DOUBLE opt-in in Klaviyo — email subscribers will
   receive a confirmation email before becoming fully subscribed.

   To repoint this form: change KLAVIYO_LIST_ID (and nothing else).
   ========================================================================== */

const KLAVIYO_CONFIG = {
  // Public API key (a.k.a. company/site ID) — safe to expose client-side.
  PUBLIC_API_KEY: "QXVh4C",

  // List that opted-in registrants are subscribed to.
  // "NCMPRBLife Community" — https://www.klaviyo.com/lists/YvRK2h
  LIST_ID: "YvRK2h",

  // Klaviyo API revision header. Update deliberately, not casually.
  REVISION: "2024-10-15",

  // Shows up in Klaviyo as the consent method / source.
  CUSTOM_SOURCE: "Warranty Registration Landing Page",
};

/* ------------------------------ Elements ------------------------------ */

const form = document.getElementById("warranty-form");
if (!form) return; // section not on this page — do nothing
const confirmation = document.getElementById("confirmation");
const submitBtn = document.getElementById("submit-btn");
const submitLabel = submitBtn.querySelector(".submit-label");
const formNote = document.getElementById("form-note");

const fields = {
  firstName: document.getElementById("first-name"),
  lastName: document.getElementById("last-name"),
  email: document.getElementById("email"),
  phone: document.getElementById("phone"),
  product: document.getElementById("product"),
  retailer: document.getElementById("retailer"),
  purchaseDate: document.getElementById("purchase-date"),
  consentEmail: document.getElementById("consent-email"),
  consentSms: document.getElementById("consent-sms"),
  honeypot: document.getElementById("hp-company"),
};

const phoneOptionalTag = document.getElementById("phone-optional-tag");

/* ----------------------------- Validation ----------------------------- */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Normalize a US-formatted phone number to E.164 (+1XXXXXXXXXX),
 * which Klaviyo requires for SMS. Returns null if it can't be done.
 * International numbers entered with a leading "+" pass through.
 */
function normalizePhone(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    return digits.length >= 8 ? "+" + digits : null;
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return "+1" + digits;
  if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
  return null;
}

/** Show a gentle inline note under a field. */
function setNote(input, message) {
  const note = document.querySelector(`[data-note-for="${input.id}"]`);
  if (!note) return;
  note.textContent = message;
  note.hidden = false;
  input.closest(".field").classList.add("has-note");
}

/** Clear the inline note for a field. */
function clearNote(input) {
  const note = document.querySelector(`[data-note-for="${input.id}"]`);
  if (!note) return;
  note.hidden = true;
  input.closest(".field").classList.remove("has-note");
}

/** Validate everything; returns true when the form may submit. */
function validate() {
  let ok = true;
  let firstInvalid = null;

  const require = (input, message) => {
    if (!input.value.trim()) {
      setNote(input, message);
      ok = false;
      firstInvalid = firstInvalid || input;
    }
  };

  require(fields.firstName, "May we have your first name?");
  require(fields.lastName, "And your last name, please.");
  require(fields.email, "We need an email to file your coverage.");
  require(fields.product, "Please select the piece you purchased.");
  require(fields.retailer, "Please tell us where you purchased.");

  if (fields.email.value.trim() && !EMAIL_RE.test(fields.email.value.trim())) {
    setNote(fields.email, "That email doesn't look quite right.");
    ok = false;
    firstInvalid = firstInvalid || fields.email;
  }

  // Phone: required and valid only when the SMS box is checked.
  if (fields.consentSms.checked) {
    const normalized = normalizePhone(fields.phone.value);
    if (!normalized) {
      setNote(fields.phone, "A valid phone number is needed for texts.");
      ok = false;
      firstInvalid = firstInvalid || fields.phone;
    }
  } else if (fields.phone.value.trim() && !normalizePhone(fields.phone.value)) {
    setNote(fields.phone, "That number doesn't look quite right.");
    ok = false;
    firstInvalid = firstInvalid || fields.phone;
  }

  if (firstInvalid) firstInvalid.focus({ preventScroll: false });
  return ok;
}

/* --------------------- Real-time, gentle validation --------------------- */

// Email: validate on blur, clear as soon as it's fixed.
fields.email.addEventListener("blur", () => {
  const v = fields.email.value.trim();
  if (v && !EMAIL_RE.test(v)) {
    setNote(fields.email, "That email doesn't look quite right.");
  }
});
fields.email.addEventListener("input", () => {
  if (EMAIL_RE.test(fields.email.value.trim())) clearNote(fields.email);
});

// Phone: same pattern.
fields.phone.addEventListener("blur", () => {
  const v = fields.phone.value.trim();
  if (v && !normalizePhone(v)) {
    setNote(fields.phone, "That number doesn't look quite right.");
  }
});
fields.phone.addEventListener("input", () => {
  if (normalizePhone(fields.phone.value)) clearNote(fields.phone);
});

// Everything else: clear its note the moment it gains a value.
[fields.firstName, fields.lastName, fields.product, fields.retailer].forEach(
  (input) => {
    input.addEventListener("input", () => {
      if (input.value.trim()) clearNote(input);
    });
    input.addEventListener("change", () => {
      if (input.value.trim()) clearNote(input);
    });
  }
);

// SMS box drives whether phone reads "optional".
fields.consentSms.addEventListener("change", () => {
  phoneOptionalTag.textContent = fields.consentSms.checked
    ? "required for texts"
    : "optional";
  if (!fields.consentSms.checked) clearNote(fields.phone);
});

/* --------------------------- Klaviyo requests --------------------------- */

/** POST a JSON:API body to a Klaviyo client endpoint. Resolves on 2xx. */
async function klaviyoPost(path, body) {
  const url = `https://a.klaviyo.com/client/${path}/?company_id=${KLAVIYO_CONFIG.PUBLIC_API_KEY}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      revision: KLAVIYO_CONFIG.REVISION,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Klaviyo error bodies are JSON:API; surface for developers only.
    const detail = await res.text().catch(() => "");
    throw new Error(`Klaviyo ${path} failed (${res.status}): ${detail}`);
  }
}

/** Call 1 — always: profile with channel-attribution custom properties. */
function buildProfilePayload(data) {
  const attributes = {
    email: data.email,
    first_name: data.firstName,
    last_name: data.lastName,
    properties: {
      warranty_registered: true,
      warranty_product: data.product,
      warranty_retailer: data.retailer,           // channel attribution
      warranty_purchase_date: data.purchaseDate || null,
      warranty_registered_at: new Date().toISOString(),
      warranty_registration_source: KLAVIYO_CONFIG.CUSTOM_SOURCE,
    },
  };
  if (data.phone) attributes.phone_number = data.phone;
  return { data: { type: "profile", attributes } };
}

/** Call 2 — only if opted in: explicit consent + list subscription. */
function buildSubscriptionPayload(data) {
  const subscriptions = {};
  if (data.consentEmail) {
    subscriptions.email = { marketing: { consent: "SUBSCRIBED" } };
  }
  if (data.consentSms) {
    subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
  }

  const profileAttributes = { email: data.email, subscriptions };
  if (data.consentSms && data.phone) profileAttributes.phone_number = data.phone;

  return {
    data: {
      type: "subscription",
      attributes: {
        custom_source: KLAVIYO_CONFIG.CUSTOM_SOURCE,
        profile: { data: { type: "profile", attributes: profileAttributes } },
      },
      relationships: {
        list: { data: { type: "list", id: KLAVIYO_CONFIG.LIST_ID } },
      },
    },
  };
}

/* ------------------------------- Submit ------------------------------- */

form.addEventListener("submit", async (event) => {
  event.preventDefault();
  formNote.hidden = true;

  // Honeypot tripped → pretend success, send nothing.
  if (fields.honeypot.value) {
    showConfirmation();
    return;
  }

  if (!validate()) return;

  const data = {
    firstName: fields.firstName.value.trim(),
    lastName: fields.lastName.value.trim(),
    email: fields.email.value.trim(),
    phone: normalizePhone(fields.phone.value),
    product: fields.product.value,
    retailer: fields.retailer.value,
    purchaseDate: fields.purchaseDate.value || null,
    consentEmail: fields.consentEmail.checked,
    consentSms: fields.consentSms.checked,
  };

  submitBtn.disabled = true;
  submitLabel.textContent = "Registering…";

  try {
    // 1. Warranty registration — always.
    await klaviyoPost("profiles", buildProfilePayload(data));

    // 2. Marketing consent — only for checked channels.
    if (data.consentEmail || data.consentSms) {
      await klaviyoPost("subscriptions", buildSubscriptionPayload(data));
    }

    showConfirmation();
  } catch (err) {
    console.error(err); // developer detail stays in the console
    formNote.textContent =
      "Something didn't go through on our end. Please try once more in a moment.";
    formNote.hidden = false;
    submitBtn.disabled = false;
    submitLabel.textContent = "Activate Warranty";
  }
});

/** Swap the form for the quiet confirmation state. */
function showConfirmation() {
  form.hidden = true;
  document.getElementById("intro").hidden = true;
  document.querySelector(".hairline").hidden = true;
  confirmation.hidden = false;
  confirmation.classList.add("is-visible");
  window.scrollTo({ top: 0, behavior: "smooth" });
}

})();
